/**
 * Product signals — what managers asked Copilot to do that Ask Redline can't do yet, plus
 * anything they suggested on purpose. Clustered by theme so the loudest gap is obvious, with the
 * sender's contact details, their recording and its transcript. Redline platform staff only.
 */
import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api, API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Sparkles, Mic, Mail, Phone, Building2, ChevronDown, ChevronUp } from "lucide-react";
import { ROLE_LABELS } from "./ctx";

const STATUS_TABS = [
  { key: "new", label: "New" },
  { key: "planned", label: "Planned" },
  { key: "shipped", label: "Shipped" },
  { key: "declined", label: "Declined" },
  { key: "", label: "All" },
];
const STATUS_CLS = { new: "bg-amber-50 text-amber-800", planned: "bg-blue-50 text-blue-700", shipped: "bg-emerald-50 text-emerald-700", declined: "bg-sand-100 text-ink/60" };
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const PAGE = 100;

export default function ProductSignals() {
  const [status, setStatus] = useState("new");
  const [theme, setTheme] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [data, setData] = useState({ items: [], themes: [], communities: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback((offset) => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (theme) p.set("theme", theme);
    if (tenantId) p.set("tenant_id", tenantId);
    p.set("limit", String(PAGE)); p.set("offset", String(offset));
    return api.get(`/admin/product-signals?${p.toString()}`);
  }, [status, theme, tenantId]);

  const load = useCallback(() => {
    setLoading(true);
    fetchPage(0).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, "Couldn't load product signals"))).finally(() => setLoading(false));
  }, [fetchPage]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { const r = await fetchPage(data.items.length); setData((d) => ({ ...r.data, items: [...d.items, ...(r.data.items || [])] })); }
    catch (e) { toast.error(errMsg(e, "Couldn't load more")); }
    setLoadingMore(false);
  };

  const patch = async (sig, body, okMsg) => {
    try {
      const r = await api.patch(`/admin/product-signals/${sig.id}`, body);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === sig.id ? { ...x, ...r.data.signal, recording: x.recording } : x)) }));
      if (okMsg) toast.success(okMsg);
      if (body.status) { load(); window.dispatchEvent(new CustomEvent("askredline:counts-changed")); }
    } catch (e) { toast.error(errMsg(e, "Couldn't update")); }
  };

  const { items, themes, communities, counts, total } = data;
  return (
    <div className="space-y-6" data-testid="product-signals-page">
      <div>
        <PageTitle title={<><Lightbulb className="h-7 w-7 text-amber-500" strokeWidth={1.75} /> Product signals</>} about={<>What managers asked Copilot to do that Ask Redline can&apos;t do yet, and what they suggested on their own. Each one carries who asked, how to reach them, and any recording they made.</>} />
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="signals-status-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.key || "all"} data-testid={`signals-tab-${t.key || "all"}`} onClick={() => setStatus(t.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${status === t.key ? "bg-ink text-white border-ink" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>
            {t.label}<span className="ml-1.5 text-xs opacity-70">{t.key === "" ? (counts.total ?? "") : (counts[t.key] ?? "")}</span>
          </button>
        ))}
      </div>

      {(themes.length > 0 || theme) && (
        <div className="rounded-2xl border border-sand-200 bg-white p-4" data-testid="signals-themes">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink/50 mb-2">By theme</div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="signals-theme-all" onClick={() => setTheme("")}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${!theme ? "bg-amber-500 text-white border-amber-500" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>All themes</button>
            {themes.map((t) => (
              <button key={t.theme} data-testid={`signals-theme-${t.theme}`} onClick={() => setTheme(t.theme === theme ? "" : t.theme)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${theme === t.theme ? "bg-amber-500 text-white border-amber-500" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>
                {t.label}<span className="ml-1.5 opacity-70">{t.count}</span>
              </button>
            ))}
          </div>
          {communities.length > 1 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Building2 className="h-3.5 w-3.5 text-ink/40" />
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="signals-community-filter"
                className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-semibold outline-none focus:border-ink">
                <option value="">Every community</option>
                {communities.map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.community} ({c.count})</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : items.length === 0 ? (
        <div data-testid="signals-empty" className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground flex flex-col items-center gap-2">
          <Lightbulb className="h-8 w-8 text-sand-300" /> Nothing here yet. When Copilot hits something it can&apos;t do, managers can send it here in one tap.
        </div>
      ) : (
        <div className="space-y-3" data-testid="signals-list">
          {items.map((s) => <SignalRow key={s.id} s={s} onPatch={patch} />)}
          {items.length < total && (
            <div className="pt-1 text-center">
              <button data-testid="signals-load-more" onClick={loadMore} disabled={loadingMore}
                className="rounded-full border border-sand-300 bg-white px-5 py-2.5 text-sm font-bold text-ink/75 hover:border-ink/30 disabled:opacity-50">
                {loadingMore ? "Loading…" : `Load more (${total - items.length} older)`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SignalRow({ s, onPatch }) {
  const [note, setNote] = useState(s.note || "");
  const [showTranscript, setShowTranscript] = useState(false);
  const rec = s.recording;
  return (
    <div data-testid={`signal-item-${s.id}`} className="bg-white rounded-2xl border border-sand-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="rounded-full bg-amber-50 text-amber-800 px-2.5 py-1 text-xs font-bold" data-testid={`signal-theme-${s.id}`}>{s.theme_label}</span>
          <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${s.source === "suggest" ? "bg-violet-50 text-violet-700" : "bg-sand-100 text-ink/70"}`}>
            {s.source === "suggest" ? <Lightbulb className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
            {s.source === "suggest" ? "Suggested" : "Copilot couldn't do it"}
          </span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${STATUS_CLS[s.status] || ""}`} data-testid={`signal-status-${s.id}`}>{s.status}</span>
          <span className="text-xs text-muted-foreground font-semibold">{s.community}</span>
        </div>
        <span className="text-xs text-muted-foreground">{fmt(s.created_at)}</span>
      </div>
      <p className="text-sm font-semibold text-ink mt-3" data-testid={`signal-summary-${s.id}`}>{s.summary}</p>
      {s.verbatim && s.verbatim !== s.summary && (
        <p className="text-xs text-ink/55 italic mt-1.5 whitespace-pre-wrap" data-testid={`signal-verbatim-${s.id}`}>They said: &ldquo;{s.verbatim}&rdquo;</p>
      )}
      {s.thread_title && <p className="text-[11px] text-ink/40 mt-1">Copilot thread: {s.thread_title}</p>}

      {rec && (
        <div className="mt-3 rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2" data-testid={`signal-recording-${s.id}`}>
          <div className="flex items-center gap-2 text-xs font-semibold text-ink/70">
            <Mic className="h-3.5 w-3.5" />
            Voice note{rec.duration_s ? ` · ${Math.floor(rec.duration_s / 60)}:${String(rec.duration_s % 60).padStart(2, "0")}` : ""}
          </div>
          <audio src={`${API}/admin/product-signals/${s.id}/recording`} controls preload="metadata" className="w-full h-9" />
          {rec.transcript && (
            <div>
              <button onClick={() => setShowTranscript((v) => !v)} data-testid={`signal-transcript-toggle-${s.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/60 hover:text-ink">
                {showTranscript ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Transcript
              </button>
              {showTranscript && <p className="mt-1 text-xs text-ink/70 whitespace-pre-wrap" data-testid={`signal-transcript-${s.id}`}>{rec.transcript}</p>}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`signal-contact-${s.id}`}>
          <span className="text-ink-700 font-semibold">{s.name}</span>
          <span className="rounded-full bg-sand-100 text-ink/70 px-2 py-0.5 font-semibold">{ROLE_LABELS[s.role] || s.role}</span>
          {s.email && <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 text-ink-700 hover:text-redline-600"><Mail className="h-3 w-3" /> {s.email}</a>}
          {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 text-ink-700 hover:text-redline-600 font-mono"><Phone className="h-3 w-3" /> {s.phone}</a>}
        </div>
        <select value={s.status} onChange={(e) => onPatch(s, { status: e.target.value }, `Marked ${e.target.value}`)} data-testid={`signal-status-select-${s.id}`}
          className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-semibold outline-none focus:border-ink">
          <option value="new">New</option><option value="planned">Planned</option><option value="shipped">Shipped</option><option value="declined">Declined</option>
        </select>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => { if (note !== (s.note || "")) onPatch(s, { note }, "Note saved"); }}
        rows={1} placeholder="Internal note (saved when you click away)" data-testid={`signal-note-${s.id}`}
        className="mt-3 w-full resize-none rounded-xl border border-sand-200 bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
    </div>
  );
}
